import { create } from "zustand"

export type DebugLogLevel = "debug" | "info" | "warn" | "error"
export type DebugLogCategory =
  | "ws.connection"
  | "ws.message"
  | "ws.reconnect"
  | "ws.heartbeat"
  | "ws.catchup"
  | "ws.send"
  | "ws.error"
  | "sync.new"
  | "sync.checkpoint"
  | "activity.connection"
  | "activity.message"
  | "activity.reconnect"
  | "activity.error"
  | "sandbox.lifecycle"
  | "sandbox.progress"
  | "sandbox.error"
  | "agent.event"
  | "agent.status"
  | "session.lifecycle"
  | "pty.connection"

export interface DebugLogEntry {
  id: number
  timestamp: number
  level: DebugLogLevel
  category: DebugLogCategory
  message: string
  details?: Record<string, unknown>
}

const MAX_ENTRIES = 1000
const DEBUG_LOG_PERSIST_SCHEMA_VERSION = 1
const DEBUG_LOG_PERSIST_STORAGE_KEY = `cord:debug-logs:v${DEBUG_LOG_PERSIST_SCHEMA_VERSION}`
/**
 * Keep a smaller persisted tail than the in-memory ring so one refresh repro can
 * survive a reload without letting debug storage meaningfully compete with other
 * browser-local cache state that shares the same sessionStorage budget.
 */
const PERSISTED_DEBUG_LOG_MAX_ENTRIES = 200
const PERSISTED_CATEGORIES = new Set<DebugLogCategory>(["sync.new", "sync.checkpoint"])
const KNOWN_LEVELS = new Set<DebugLogLevel>(["debug", "info", "warn", "error"])
const KNOWN_CATEGORIES = new Set<DebugLogCategory>([
  "ws.connection",
  "ws.message",
  "ws.reconnect",
  "ws.heartbeat",
  "ws.catchup",
  "ws.send",
  "ws.error",
  "sync.new",
  "sync.checkpoint",
  "activity.connection",
  "activity.message",
  "activity.reconnect",
  "activity.error",
  "sandbox.lifecycle",
  "sandbox.progress",
  "sandbox.error",
  "agent.event",
  "agent.status",
  "session.lifecycle",
  "pty.connection",
])

let nextId = 1

type PersistedDebugLogEnvelope = {
  schemaVersion: number
  entries: DebugLogEntry[]
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null
  }

  return value as Record<string, unknown>
}

function asFiniteInteger(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null
  }

  return Math.floor(value)
}

function asString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function resolveDebugLogStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null
  }

  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

function removePersistedEntries(storage: Storage): void {
  try {
    storage.removeItem(DEBUG_LOG_PERSIST_STORAGE_KEY)
  } catch {
    // Debug-log persistence is best effort only.
  }
}

function trimPersistedEntries(entries: DebugLogEntry[]): DebugLogEntry[] {
  return entries
    .filter((entry) => PERSISTED_CATEGORIES.has(entry.category))
    .slice(-PERSISTED_DEBUG_LOG_MAX_ENTRIES)
}

function writePersistedEntries(storage: Storage, entries: DebugLogEntry[]): DebugLogEntry[] {
  let retainedEntries = trimPersistedEntries(entries)

  if (retainedEntries.length === 0) {
    removePersistedEntries(storage)
    return []
  }

  while (retainedEntries.length > 0) {
    const envelope: PersistedDebugLogEnvelope = {
      schemaVersion: DEBUG_LOG_PERSIST_SCHEMA_VERSION,
      entries: retainedEntries,
    }

    try {
      storage.setItem(DEBUG_LOG_PERSIST_STORAGE_KEY, JSON.stringify(envelope))
      return retainedEntries
    } catch {
      retainedEntries = retainedEntries.slice(1)
    }
  }

  removePersistedEntries(storage)
  return []
}

function parsePersistedEntry(value: unknown): DebugLogEntry | null {
  const row = asRecord(value)
  if (!row) {
    return null
  }

  const id = asFiniteInteger(row.id)
  const timestamp = asFiniteInteger(row.timestamp)
  const level = asString(row.level)
  const category = asString(row.category)
  const message = asString(row.message)
  const rawDetails = row.details === undefined ? undefined : asRecord(row.details)
  if (
    id === null ||
    timestamp === null ||
    message === null ||
    !level ||
    !KNOWN_LEVELS.has(level as DebugLogLevel) ||
    !category ||
    !KNOWN_CATEGORIES.has(category as DebugLogCategory)
  ) {
    return null
  }

  return {
    id,
    timestamp,
    level: level as DebugLogLevel,
    category: category as DebugLogCategory,
    message,
    details: rawDetails ?? undefined,
  }
}

function loadPersistedEntries(): DebugLogEntry[] {
  const storage = resolveDebugLogStorage()
  if (!storage) {
    return []
  }

  try {
    const raw = storage.getItem(DEBUG_LOG_PERSIST_STORAGE_KEY)
    if (!raw) {
      return []
    }

    const parsed = JSON.parse(raw) as unknown
    const envelope = asRecord(parsed)
    if (!envelope) {
      removePersistedEntries(storage)
      return []
    }

    const schemaVersion = asFiniteInteger(envelope.schemaVersion)
    if (schemaVersion !== DEBUG_LOG_PERSIST_SCHEMA_VERSION) {
      removePersistedEntries(storage)
      return []
    }

    if (!Array.isArray(envelope.entries)) {
      removePersistedEntries(storage)
      return []
    }

    const parsedEntries = envelope.entries
      .map((entry) => parsePersistedEntry(entry))
      .filter((entry): entry is DebugLogEntry => entry !== null)
    const persistedEntries = trimPersistedEntries(parsedEntries)
    const requiresSanitization =
      parsedEntries.length !== envelope.entries.length ||
      persistedEntries.length !== parsedEntries.length

    if (persistedEntries.length === 0) {
      removePersistedEntries(storage)
      return []
    }

    return requiresSanitization
      ? writePersistedEntries(storage, persistedEntries)
      : persistedEntries
  } catch {
    removePersistedEntries(storage)
    return []
  }
}

let persistedEntries = loadPersistedEntries()
const initialEntries = persistedEntries.slice()
nextId = initialEntries.reduce((maxId, entry) => Math.max(maxId, entry.id), 0) + 1

interface DebugLogState {
  entries: DebugLogEntry[]
  /** Monotonic counter for efficient change detection */
  revision: number
  /** Category filters -- null means show all */
  categoryFilter: Set<DebugLogCategory> | null
  /** Level filters -- null means show all */
  levelFilter: Set<DebugLogLevel> | null

  push: (
    level: DebugLogLevel,
    category: DebugLogCategory,
    message: string,
    details?: Record<string, unknown>,
  ) => void
  clear: () => void
  setCategoryFilter: (filter: Set<DebugLogCategory> | null) => void
  setLevelFilter: (filter: Set<DebugLogLevel> | null) => void
}

export const useDebugLogs = create<DebugLogState>()((set, get) => ({
  entries: initialEntries,
  revision: 0,
  categoryFilter: null,
  levelFilter: null,

  push: (level, category, message, details) => {
    const entry: DebugLogEntry = {
      id: nextId++,
      timestamp: Date.now(),
      level,
      category,
      message,
      details,
    }
    const state = get()
    const next = [...state.entries, entry]
    // Ring buffer: drop oldest entries when exceeding max
    if (next.length > MAX_ENTRIES) {
      next.splice(0, next.length - MAX_ENTRIES)
    }

    if (PERSISTED_CATEGORIES.has(category)) {
      const storage = resolveDebugLogStorage()
      if (storage) {
        persistedEntries = writePersistedEntries(storage, [...persistedEntries, entry])
      }
    }

    set({ entries: next, revision: state.revision + 1 })
  },

  clear: () => {
    const storage = resolveDebugLogStorage()
    persistedEntries = []
    if (storage) {
      removePersistedEntries(storage)
    }
    set({ entries: [], revision: 0 })
  },

  setCategoryFilter: (filter) => set({ categoryFilter: filter }),
  setLevelFilter: (filter) => set({ levelFilter: filter }),
}))

// ── Convenience helpers (callable outside React) ────────────────────────

export function debugLog(
  level: DebugLogLevel,
  category: DebugLogCategory,
  message: string,
  details?: Record<string, unknown>,
) {
  useDebugLogs.getState().push(level, category, message, details)
}

export function debugInfo(category: DebugLogCategory, message: string, details?: Record<string, unknown>) {
  debugLog("info", category, message, details)
}

export function debugWarn(category: DebugLogCategory, message: string, details?: Record<string, unknown>) {
  debugLog("warn", category, message, details)
}

export function debugError(category: DebugLogCategory, message: string, details?: Record<string, unknown>) {
  debugLog("error", category, message, details)
}
